import { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { makeStyles } from 'tss-react/mui';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useLatest, usePrevious } from 'react-use';
import DateField from '../forms/date-field';
import TimeField from '../forms/time-field';
import TextField from '../forms/text-field';
import { momentParse } from '../../utils/time';
import type { EventFormValues } from './form';

const useStyles = makeStyles()((theme) => ({
  dateAndTime: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    gap: theme.spacing(2),
    '& *.MuiFormControl-root': {
      margin: '0 !important',
    },
  },
  field: {
    margin: 0,
  },
  button: {
    margin: theme.spacing(0, 0, 1, 0),
  },
  buttonIcon: {
    margin: theme.spacing(0, 0.5, 0, 0),
  },
}));

type Props = {
  disabled: boolean;
  forceTouched: boolean;
};

const EventFormTime = ({ disabled, forceTouched }: Props) => {
  const { classes } = useStyles();
  const {
    setFieldValue,
    values: { startDate, startTime, endDate, endTime },
  } = useFormikContext<EventFormValues>();
  // Derived from the values (not captured at mount) so end values that arrive
  // after mount — e.g. DraftAutosave restoring an unsaved draft into Formik —
  // reveal the section instead of staying hidden (and at risk of being
  // clobbered by the toggle's start-time defaults). `manuallyOpen` keeps the
  // section visible while both fields are empty or mid-edit.
  const [manuallyOpen, setManuallyOpen] = useState(false);
  const open = manuallyOpen || Boolean(endDate || endTime);
  const today = useMemo(() => momentParse(), []);

  const toggleOpen = useCallback(() => {
    if (open) {
      setFieldValue('endDate', null);
      setFieldValue('endTime', null);
    } else {
      const momentStartDate = moment(startDate);
      const momentStartTime = moment(startTime);
      const endDate = momentStartDate.isValid() ? momentStartDate : null;
      const endTime = momentStartTime.isValid() ? momentStartTime : null;
      setFieldValue('endDate', endDate);
      setFieldValue('endTime', endTime);
    }
    setManuallyOpen(!open);
  }, [open, startDate, startTime, setFieldValue]);

  const handleIsValidDate = useCallback((currentDate) => {
    return currentDate.isSameOrAfter(today, 'day');
  }, [today]);

  // make sure start and end dates are in order
  const latestStartDate = useLatest(startDate);
  const previousStartDate = usePrevious(startDate);
  const latestPreviousStartDate = useLatest(previousStartDate);
  const latestEndDate = useLatest(endDate);
  useEffect(() => {
    if (!startDate || !latestEndDate.current) {
      return;
    }
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(latestEndDate.current);
    if (!momentStartDate.isValid() || !momentEndDate.isValid()) {
      return;
    }
    if (momentEndDate.isBefore(momentStartDate, 'day')) {
      const previousMomentStartDate = moment(latestPreviousStartDate.current);
      const previousDiff = momentEndDate.diff(previousMomentStartDate);
      setFieldValue('endDate', moment(momentStartDate).add(previousDiff));
    }
  }, [startDate, latestEndDate, latestPreviousStartDate, setFieldValue]);
  useEffect(() => {
    if (!latestStartDate.current || !endDate) {
      return;
    }
    const momentStartDate = moment(latestStartDate.current);
    const momentEndDate = moment(endDate);
    if (!momentStartDate.isValid() || !momentEndDate.isValid()) {
      return;
    }
    if (momentEndDate.isBefore(momentStartDate, 'day')) {
      setFieldValue('startDate', momentEndDate);
    }
  }, [endDate, latestStartDate, setFieldValue]);

  return (
    <Fragment>
      <Typography variant="h2">Date and Time</Typography>
      <div className={classes.dateAndTime}>
        <DateField
          name="startDate"
          label="Start date"
          disabled={disabled}
          touched={forceTouched}
          className={classes.field}
          isValidDate={handleIsValidDate}
        />
        <TimeField
          name="startTime"
          label="Start time"
          disabled={disabled}
          touched={forceTouched}
          className={classes.field}
        />
      </div>
      <TextField
        name="startAtDetails"
        label="Start time details (optional)"
        helperText="Example: Meet at 6, leave at 6:30"
        disabled={disabled}
        touched={forceTouched}
      />
      {open && (
        <div className={classes.dateAndTime}>
          <DateField
            name="endDate"
            label="End date (optional)"
            disabled={disabled}
            touched={forceTouched}
            isValidDate={handleIsValidDate}
          />
          <TimeField
            name="endTime"
            label="End time (optional)"
            disabled={disabled}
            touched={forceTouched}
          />
        </div>
      )}
      <Button
        variant="outlined"
        onClick={toggleOpen}
        disabled={disabled}
        className={classes.button}
      >
        {open
          ? <RemoveIcon className={classes.buttonIcon} />
          : <AddIcon className={classes.buttonIcon} />}
        End Date and Time (optional)
      </Button>
    </Fragment>
  );
};

export default EventFormTime;
